import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Download, RotateCcw, Image as ImageIcon } from "lucide-react";
import { WERTE, shuffle } from "@/lib/werte";
import { generateWertePdf } from "@/lib/wertePdf";
import { downloadWerteBild } from "@/lib/werteBild";
import SiteFooter from "@/components/site/sections/SiteFooter";

const STORAGE_KEY = "limonata-kernwerte";
const PROGRESS_KEY = "limonata-werte-progress";

const loadProgress = () => {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    const p = raw ? JSON.parse(raw) : null;
    if (p && Array.isArray(p.pairs) && p.pairs.length > 0 && p.idx < p.pairs.length) return p;
    return null;
  } catch {
    return null;
  }
};

const saveProgress = (p) => {
  try {
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable */
  }
};

const clearProgress = () => {
  try {
    localStorage.removeItem(PROGRESS_KEY);
  } catch {
    /* storage unavailable */
  }
};

const loadSaved = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const vals = raw ? JSON.parse(raw) : null;
    return Array.isArray(vals) && vals.length === 4 ? vals : null;
  } catch {
    return null;
  }
};

const saveResult = (vals) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(vals));
  } catch {
    /* storage unavailable */
  }
};

const clearResult = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
};

const pairKey = (a, b) => [a, b].sort().join("|");

const makeRound = (pool, jokeredKeys) => {
  const shuffled = shuffle(pool);
  const allPairs = [];
  const rest = [...shuffled];
  while (rest.length >= 2) {
    const a = rest.shift();
    let pi = rest.findIndex((b) => !jokeredKeys.has(pairKey(a, b)));
    if (pi === -1) pi = 0;
    allPairs.push([a, rest[pi]]);
    rest.splice(pi, 1);
  }
  const duels = Math.min(allPairs.length, pool.length - 4);
  const extra = allPairs.slice(duels).flat();
  return { pairs: allPairs.slice(0, duels), byes: [...rest, ...extra] };
};

const ValueCard = ({ value, side, onPick, duelKey }) => (
  <motion.button
    key={`${duelKey}-${value}`}
    initial={{ opacity: 0, y: 40, rotate: side === "l" ? -7 : 7 }}
    animate={{ opacity: 1, y: 0, rotate: side === "l" ? -1.2 : 1.2 }}
    exit={{ opacity: 0, y: -40 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ scale: 1.04, rotate: 0 }}
    whileTap={{ scale: 0.97 }}
    onClick={onPick}
    data-testid={`werte-card-${side}`}
    className="flex min-h-[150px] w-full items-center justify-center rounded-sm border-2 border-cognac/50 bg-white/70 px-6 py-10 shadow-[0_24px_50px_-28px_rgba(62,39,35,0.5)] transition-colors duration-300 hover:border-cognac-2 hover:bg-white md:min-h-[220px]"
    style={{ transformOrigin: "bottom center" }}
  >
    <span className="hyphens-auto break-words font-heading text-2xl font-700 uppercase tracking-tight text-espresso md:text-4xl" style={{ fontWeight: 700 }} lang="de">
      {value}
    </span>
  </motion.button>
);

const Progress = ({ round, duelIdx, duelCount, poolSize, totalPct }) => (
  <div className="mb-10" data-testid="werte-progress">
    <div className="mb-3 flex items-baseline justify-between">
      <p className="font-heading text-xs font-700 uppercase tracking-[0.25em] text-cognac-2" style={{ fontWeight: 700 }}>
        Runde {round} · Duell {duelIdx + 1} von {duelCount}
      </p>
      <p className="font-heading text-xs uppercase tracking-[0.2em] text-espresso/60">
        Noch {poolSize} Werte im Spiel
      </p>
    </div>
    <div className="h-1 w-full overflow-hidden rounded-full bg-cognac/20">
      <div
        className="h-full rounded-full bg-cognac-2 transition-all duration-500"
        style={{ width: `${(duelIdx / duelCount) * 100}%` }}
      />
    </div>
    <div className="mt-4 flex items-baseline justify-between" data-testid="werte-progress-total">
      <p className="font-heading text-[11px] uppercase tracking-[0.25em] text-espresso/50">
        Gesamtfortschritt
      </p>
      <p className="font-heading text-[11px] font-700 uppercase tracking-[0.2em] text-cognac-2" style={{ fontWeight: 700 }}>
        {totalPct} %
      </p>
    </div>
    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-cognac/20">
      <div
        className="h-full rounded-full bg-espresso transition-all duration-500"
        style={{ width: `${totalPct}%` }}
      />
    </div>
  </div>
);

const Intro = ({ onStart }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mx-auto max-w-2xl text-center">
    <p className="mb-6 font-heading text-xs font-700 uppercase tracking-[0.35em] text-cognac-2" style={{ fontWeight: 700 }}>
      Wer bin ich?
    </p>
    <h1 className="mb-8 font-heading text-4xl font-700 uppercase leading-[1.02] tracking-tight text-espresso sm:text-5xl lg:text-6xl" style={{ fontWeight: 700 }}>
      Dein Werte-Check
    </h1>
    <p className="mb-4 font-body text-lg leading-[1.75] text-[#241a15] md:text-xl">
      Es treten immer zwei Werte gegeneinander an. Wähle jeweils den aus, der Dir wichtiger ist. Die Gewinner spielen weiter, bis Deine 4 Kernwerte feststehen.
    </p>
    <p className="mb-10 font-body text-base italic leading-[1.7] text-cognac-2">
      Falls Du Dich mal wirklich nicht entscheiden kannst: Du hast bis zu 5 Joker. Damit kommen beide Werte weiter.
    </p>
    <button
      onClick={onStart}
      data-testid="werte-start-btn"
      className="group inline-flex items-center gap-3 rounded-full bg-cognac-2 px-10 py-4 font-heading text-sm font-700 uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_-15px_rgba(142,78,43,0.6)] transition-all duration-300 hover:scale-[1.03] hover:bg-espresso"
      style={{ fontWeight: 700 }}
    >
      Los geht's
    </button>
    <p className="mt-6 font-heading text-[11px] uppercase tracking-[0.25em] text-espresso/50">64 Werte · ca. 10–15 Minuten</p>
  </motion.div>
);

const Result = ({ values, onRestart }) => {
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      await generateWertePdf(values);
    } finally {
      setBusy(false);
    }
  };
  const downloadImg = async () => {
    setImgBusy(true);
    try {
      await downloadWerteBild(values);
    } finally {
      setImgBusy(false);
    }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mx-auto max-w-3xl text-center" data-testid="werte-result">
      <p className="mb-6 font-heading text-xs font-700 uppercase tracking-[0.35em] text-cognac-2" style={{ fontWeight: 700 }}>
        Geschafft!
      </p>
      <h1 className="mb-12 font-heading text-4xl font-700 uppercase leading-[1.02] tracking-tight text-espresso sm:text-5xl lg:text-6xl" style={{ fontWeight: 700 }}>
        Deine 4 Kernwerte
      </h1>
      <div className="mb-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {values.map((v, i) => (
          <motion.div
            key={v}
            initial={{ opacity: 0, y: 40, rotate: i % 2 ? 6 : -6 }}
            animate={{ opacity: 1, y: 0, rotate: i % 2 ? 1.5 : -1.5 }}
            transition={{ delay: 0.15 + i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "bottom center" }}
            data-testid={`werte-result-card-${i}`}
            className="flex min-h-[130px] items-center justify-center rounded-sm border-2 border-cognac-2 bg-gold px-4 py-8 shadow-[0_24px_50px_-28px_rgba(62,39,35,0.55)]"
          >
            <span className={`hyphens-auto break-words font-heading font-700 uppercase tracking-tight text-espresso ${v.length > 11 ? "text-base" : "text-xl"}`} style={{ fontWeight: 700 }} lang="de">
              {v}
            </span>
          </motion.div>
        ))}
      </div>
      <div className="mx-auto mb-12 max-w-2xl rounded-sm border border-cognac/30 bg-white/50 px-7 py-8 text-left" data-testid="werte-reflexion">
        <p className="mb-5 font-heading text-xs font-700 uppercase tracking-[0.25em] text-cognac-2" style={{ fontWeight: 700 }}>
          Zum Nachdenken
        </p>
        <ul className="space-y-4">
          {values.map((v) => (
            <li key={v} className="flex gap-3">
              <span className="mt-[0.8em] h-1.5 w-1.5 shrink-0 rounded-full bg-cognac-2" />
              <p className="font-body text-base leading-[1.7] text-[#241a15] md:text-lg">
                Was bedeutet <strong className="text-espresso" style={{ fontWeight: 600 }}>{v}</strong> für Dich persönlich? Wie definierst Du {v}?
              </p>
            </li>
          ))}
        </ul>
        <p className="mt-6 font-body text-base italic leading-[1.7] text-cognac-2">
          Lade Dir das Dokument herunter – mit noch mehr Reflexionsfragen und Platz für Deine Gedanken.
        </p>
      </div>
      <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
        <button
          onClick={download}
          disabled={busy}
          data-testid="werte-download-btn"
          className="inline-flex items-center gap-3 rounded-full bg-cognac-2 px-9 py-4 font-heading text-sm font-700 uppercase tracking-[0.18em] text-white shadow-[0_18px_40px_-15px_rgba(142,78,43,0.6)] transition-all duration-300 hover:scale-[1.03] hover:bg-espresso disabled:opacity-60"
          style={{ fontWeight: 700 }}
        >
          <Download className="h-4 w-4" />
          {busy ? "Wird erstellt…" : "Als PDF herunterladen"}
        </button>
        <button
          onClick={downloadImg}
          disabled={imgBusy}
          data-testid="werte-bild-btn"
          className="inline-flex items-center gap-3 rounded-full border-2 border-cognac-2 px-9 py-4 font-heading text-sm font-700 uppercase tracking-[0.18em] text-cognac-2 transition-all duration-300 hover:scale-[1.03] hover:bg-cognac-2 hover:text-white disabled:opacity-60"
          style={{ fontWeight: 700 }}
        >
          <ImageIcon className="h-4 w-4" />
          {imgBusy ? "Wird erstellt…" : "Als Bild fürs Handy"}
        </button>
        <button
          onClick={onRestart}
          data-testid="werte-restart-btn"
          className="inline-flex items-center gap-2 rounded-full border border-cognac/50 px-7 py-4 font-heading text-xs font-600 uppercase tracking-[0.18em] text-espresso/70 transition-colors duration-300 hover:border-cognac-2 hover:text-espresso"
          style={{ fontWeight: 600 }}
        >
          <RotateCcw className="h-4 w-4" />
          Nochmal spielen
        </button>
      </div>
    </motion.div>
  );
};

const initState = () => {
  const savedVals = loadSaved();
  if (savedVals) return { phase: "done", finalVals: savedVals, progress: null };
  const progress = loadProgress();
  if (progress) return { phase: "play", finalVals: null, progress };
  return { phase: "intro", finalVals: null, progress: null };
};

export default function WerteCheck() {
  const [init] = useState(initState);
  const p = init.progress;
  const [phase, setPhase] = useState(init.phase);
  const [round, setRound] = useState(p ? p.round : 1);
  const [pairs, setPairs] = useState(p ? p.pairs : []);
  const [byes, setByes] = useState(p ? p.byes : []);
  const [idx, setIdx] = useState(p ? p.idx : 0);
  const [winners, setWinners] = useState(p ? p.winners : []);
  const [jokers, setJokers] = useState(p ? p.jokers : 5);
  const [poolSize, setPoolSize] = useState(p ? p.poolSize : WERTE.length);
  const [jokered, setJokered] = useState(p ? p.jokered || [] : []);
  const [finalVals, setFinalVals] = useState(init.finalVals);

  useEffect(() => {
    if (phase === "play" && pairs.length > 0) {
      saveProgress({ round, pairs, byes, idx, winners, jokers, poolSize, jokered });
    }
  }, [phase, round, pairs, byes, idx, winners, jokers, poolSize, jokered]);

  const startRound = (pool, roundNo, jk = jokered) => {
    if (pool.length <= 4) {
      saveResult(pool);
      clearProgress();
      setFinalVals(pool);
      setPhase("done");
      return;
    }
    const r = makeRound(pool, new Set(jk));
    setPairs(r.pairs);
    setByes(r.byes);
    setWinners([]);
    setIdx(0);
    setRound(roundNo);
    setPoolSize(pool.length);
    setPhase("play");
  };

  const advance = (newWinners, jk = jokered) => {
    if (idx + 1 < pairs.length) {
      setWinners(newWinners);
      setIdx(idx + 1);
    } else {
      startRound([...newWinners, ...byes], round + 1, jk);
    }
  };

  const pick = (value) => advance([...winners, value]);
  const useJoker = () => {
    const newJokered = [...jokered, pairKey(pairs[idx][0], pairs[idx][1])];
    setJokered(newJokered);
    setJokers(jokers - 1);
    advance([...winners, ...pairs[idx]], newJokered);
  };

  const restart = () => {
    clearResult();
    clearProgress();
    setJokers(5);
    setJokered([]);
    setFinalVals(null);
    setPhase("intro");
  };

  const pair = pairs[idx];
  const alive = winners.length + byes.length + (pairs.length - idx) * 2;
  const totalPct = Math.min(100, Math.round(((WERTE.length - alive) / (WERTE.length - 4)) * 100));

  return (
    <main className="grain flex min-h-screen flex-col bg-paper" data-testid="werte-check-page">
      <div className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-6 pb-24 pt-10 md:px-10">
        <p className="mb-14 text-center font-heading text-sm font-700 uppercase tracking-[0.4em] text-espresso" style={{ fontWeight: 700 }}>
          LIMONATA COACHING
        </p>

        {phase === "intro" && <Intro onStart={() => startRound(WERTE, 1)} />}

        {phase === "play" && pair && (
          <div>
            <Progress round={round} duelIdx={idx} duelCount={pairs.length} poolSize={poolSize} totalPct={totalPct} />
            <h2 className="mb-5 text-center font-heading text-xl font-600 uppercase tracking-tight text-espresso md:text-2xl" style={{ fontWeight: 600 }}>
              Was ist Dir wichtiger?
            </h2>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <p className="font-body text-base leading-[1.7] text-[#241a15]/85">
                Wähle den Wert, der Dir insgesamt in Deinem Leben (nicht nur Arbeit) wichtiger ist. Denke nicht zu lange darüber nach, sondern folge Deinem Impuls.
              </p>
              <p className="mt-3 font-body text-base italic leading-[1.7] text-cognac-2">
                Wichtig: Das sind DEINE Werte und es ist Dein Leben. Wähle, was DIR wichtig ist. Nicht, was Deine Eltern, Partner:in, Freund:innen etc. von Dir erwarten würden, was Du auswählst.
              </p>
            </div>
            <div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-[1fr_auto_1fr] md:gap-8">
              <AnimatePresence mode="popLayout">
                <ValueCard value={pair[0]} side="l" duelKey={`${round}-${idx}`} onPick={() => pick(pair[0])} />
              </AnimatePresence>
              <div className="flex items-center justify-center">
                <span className="font-heading text-sm font-700 uppercase tracking-[0.2em] text-cognac-2" style={{ fontWeight: 700 }}>
                  vs.
                </span>
              </div>
              <AnimatePresence mode="popLayout">
                <ValueCard value={pair[1]} side="r" duelKey={`${round}-${idx}`} onPick={() => pick(pair[1])} />
              </AnimatePresence>
            </div>
            <div className="mt-12 text-center">
              <button
                onClick={useJoker}
                disabled={jokers === 0}
                data-testid="werte-joker-btn"
                className="inline-flex items-center gap-2 rounded-full border border-cognac/50 px-7 py-3 font-heading text-xs font-600 uppercase tracking-[0.16em] text-espresso/75 transition-all duration-300 enabled:hover:border-cognac-2 enabled:hover:text-espresso disabled:cursor-not-allowed disabled:opacity-40"
                style={{ fontWeight: 600 }}
              >
                <Sparkles className="h-4 w-4 text-cognac-2" />
                Beide sind mir wichtig · {jokers}× Joker übrig
              </button>
            </div>
          </div>
        )}

        {phase === "done" && finalVals && <Result values={finalVals} onRestart={restart} />}
      </div>
      <SiteFooter />
    </main>
  );
}
